import { useMutation } from '@src/query';


/**
 * Inventory list-page mutation hooks (T10): bulk actions, the CopyModal
 * mark-listed channel write, saved-filter create/delete and the CSV export.
 * Each wraps the shared `useMutation` engine with the injected-`queryClient`
 * `mutateFn({ apiClient, queryClient })` contract — the corelib query singleton
 * is NOT imported here. Invalidation runs inside `mutateFn` so the exact parts
 * cache key prefix `['root','','inventory','parts',sellerId]` (and its
 * `'saved-filters'` child key) is preserved byte-for-byte against the original
 * inline handlers.
 *
 * The list page builds `partsKeyPrefix` once
 * (`['root','','inventory','parts',sellerId]`) and threads it in as
 * `keyPrefix`, so every hook invalidates that exact key — identical to the
 * original inline mutations. Saved-filter writes invalidate
 * `[...keyPrefix,'saved-filters']`, matching the original.
 *
 * Mirrors `query/garage/useDonorMutations.js`: one
 * `query/<area>/use<Thing>Mutations.js` module re-exported through the
 * `@src/query` barrel.
 */

/**
 * Bulk action over the current selection. Variables: `{ sellerId, keyPrefix,
 * ...payload }` where `payload` is `{ action, ids, status, channel }`. POSTs the
 * bulk endpoint then invalidates the parts list (`keyPrefix`); returns the
 * response. The caller clears `rowSelection` in `onSuccess` — selection
 * semantics unchanged.
 */
function useBulkPartsMutation(params) {
  return useMutation({
    mutateFn: ({ apiClient, queryClient, sellerId, keyPrefix, ...payload }) => apiClient
      .request({ url: `/inventory/parts/${sellerId}/bulk/`, method: 'POST', payload })
      .then((data) => {
        queryClient.invalidateQueries({ queryKey: keyPrefix });
        return data;
      }),
    ...params,
  });
}


/**
 * Set a single part's channel state (CopyModal "mark listed"). Variables:
 * `{ sellerId, partId, keyPrefix, ...payload }` where `payload` is
 * `{ channel, live }`. POSTs the channel endpoint then invalidates the parts
 * list (`keyPrefix`); returns the response.
 */
function usePartChannelMutation(params) {
  return useMutation({
    mutateFn: ({ apiClient, queryClient, sellerId, partId, keyPrefix, ...payload }) => apiClient
      .request({ url: `/inventory/parts/${sellerId}/${partId}/channels/`, method: 'POST', payload })
      .then((data) => {
        queryClient.invalidateQueries({ queryKey: keyPrefix });
        return data;
      }),
    ...params,
  });
}


/**
 * Save the current filter query under a name. Variables: `{ sellerId, keyPrefix,
 * name, query }`. POSTs the saved-filters endpoint then invalidates the
 * saved-filters list (`[...keyPrefix,'saved-filters']`); returns the response.
 */
function useSaveFilterMutation(params) {
  return useMutation({
    mutateFn: ({ apiClient, queryClient, sellerId, keyPrefix, name, query }) => apiClient
      .request({
        url: `/inventory/parts/${sellerId}/saved-filters/`,
        method: 'POST',
        payload: { name, query },
      })
      .then((data) => {
        queryClient.invalidateQueries({ queryKey: [...keyPrefix, 'saved-filters'] });
        return data;
      }),
    ...params,
  });
}


/**
 * Delete a saved filter. Variables: `{ sellerId, id, keyPrefix }`. DELETEs then
 * invalidates the saved-filters list (`[...keyPrefix,'saved-filters']`).
 */
function useDeleteFilterMutation(params) {
  return useMutation({
    mutateFn: ({ apiClient, queryClient, sellerId, id, keyPrefix }) => apiClient
      .request({ url: `/inventory/parts/${sellerId}/saved-filters/${id}/`, method: 'DELETE' })
      .then(() => queryClient.invalidateQueries({ queryKey: [...keyPrefix, 'saved-filters'] })),
    ...params,
  });
}


/**
 * Export the current inventory view as CSV. Variables: `{ sellerId, params }`
 * where `params` are the shared list query params. Returns the raw CSV text; the
 * caller turns it into a Blob download. No cache invalidation (read-only).
 */
function useInventoryExportMutation(params) {
  return useMutation({
    mutateFn: ({ apiClient, sellerId, params: queryParams }) => apiClient.request({
      url: `/inventory/parts/${sellerId}/export/`,
      payload: queryParams,
    }),
    ...params,
  });
}


export {
  useBulkPartsMutation,
  useDeleteFilterMutation,
  useInventoryExportMutation,
  usePartChannelMutation,
  useSaveFilterMutation,
};
